import type { JourneyDetailDto } from "@/api/endpoints";
import { InfoTooltip } from "@/components/InfoTooltip";
import { fmtDateOnly, fmtETDate, fmtWholeBd } from "@/lib/format";
import { ATTENTION_CLASS, ATTENTION_LABEL, attentionTip } from "@/features/operations/operationsLabels";
import { CopyWoButton } from "@/features/requestDetail/components/CopyWoButton";
import { StartRiskPill } from "@/features/operations/components/StartRiskCells";
import { startRiskTip } from "@/features/operations/operationsLabels";
import { CARRIED_BY_LABEL, JOURNEY_STATE_LABEL, PEER_GROUP_TIP } from "../journeyLabels";
import { journeyAttention } from "../journeyView";

interface Props {
  journey: JourneyDetailDto;
  onAskClaude?: () => void;
}

const FBO_REQUEST_URL = "https://www.flexbackoffice.com/next/mfe/sync/requests/";
/** FBO's work-order page: assignment, work order, version (Wendy's link, 2026-10-27). */
const fboWorkOrderUrl = (assignmentId: number, workOrderId: number, versionId: number) =>
  `https://www.flexbackoffice.com/next/workorder/${assignmentId}/${workOrderId}/${versionId}/core/`;

/**
 * Who and what the journey is, its status (In progress / Completed / Canceled, the journey's, not any
 * record's FBO status), the attention of the record carrying it, the time so far against the peer
 * median, and the actions: Open in FBO (the request), Copy WO #, Ask Claude why.
 */
export function JourneyHeader({ journey: d, onAskClaude }: Props) {
  const attention = journeyAttention(d);
  const total = d.stages.find((s) => s.key === "total");
  const ratio = total?.bd != null && total.peerMedianBd ? total.bd / total.peerMedianBd : null;
  const over = d.state === "complete" || d.state === "canceled";
  const wo = d.workOrder;
  // "started" is the journey start, the same date the Operations drawers call Started; the contract start is named as such.
  const contractStart = wo?.startDate ?? d.intake?.startDate ?? null;
  const sub = [
    wo ? `WO ${wo.workOrderNumber ?? wo.workOrderId}` : null,
    d.client,
    d.request?.templateName ?? null,
    total?.start ? `started ${fmtETDate(total.start)}` : null,
    contractStart ? `contract starts ${fmtDateOnly(contractStart)}` : null,
  ].filter(Boolean).join(" · ");
  return (
    <div className="detail-header journey-head">
      <div className="row1">
        <div>
          <h2>
            {d.worker ?? "Unknown worker"}
            <span className="ops-pill kind">{d.kind} journey</span>
          </h2>
          <div className="sub">{sub || "—"}</div>
          <div className="sub carried">
            {d.state === "complete" ? (
              <>Journey complete{d.kind === "Termination" && wo?.offboardingCompletionDate ? ` · WO terminated ${fmtETDate(wo.offboardingCompletionDate)}` : wo?.approveDateTime ? ` · WO approved ${fmtETDate(wo.approveDateTime)}` : ""}</>
            ) : d.state === "canceled" ? (
              <>Journey canceled{d.intake?.endedDateTime ? ` · ${fmtETDate(d.intake.endedDateTime)}` : ""}</>
            ) : (
              <>Carried by <b>{CARRIED_BY_LABEL[d.carrier]}</b></>
            )}
          </div>
        </div>
        <div className="right">
          <div className="pills">
            <span className={`status-pill ${d.state === "complete" ? "completed" : d.state === "canceled" ? "canceled" : "inprogress"}`} role="status" aria-label={`Journey: ${JOURNEY_STATE_LABEL[d.state]}`}>
              <span className="dot" />{JOURNEY_STATE_LABEL[d.state]}
            </span>
            {attention && !over && (
              <span className={`ops-pill att-${ATTENTION_CLASS[attention.state] ?? "unknown"}`} data-testid="journey-attention">
                <i className="dot" />
                {ATTENTION_LABEL[attention.state] ?? attention.state}
                {attention.reason && <span className="reason"> · {attention.reason}</span>}
                <InfoTooltip inline text={attentionTip(attention.state, d.stuckAfterBd, d.blockedAfterBd)} />
              </span>
            )}
            {!over && (d.startRisk === "atRisk" || d.startRisk === "breached") && (
              <span className="journey-start-risk" data-testid="journey-start-risk">
                <StartRiskPill risk={d.startRisk} leadBd={d.startLeadBd} />
                <InfoTooltip inline text={startRiskTip(d.startRisk, d.atRiskAfterBd, d.breachedAfterBd)} />
              </span>
            )}
          </div>
          {total?.bd != null && (
            <div className="peer">
              <b>{fmtWholeBd(total.bd)}</b> {over ? "in total" : "so far"}
              {total.peerMedianBd != null ? (
                <> · {ratio!.toFixed(1)}× peer median ({total.peerMedianBd.toFixed(2)} bd over {d.peerGroup.sampleSize} similar requests)</>
              ) : (
                <> · no peer median (fewer than five similar completed requests)</>
              )}
              <InfoTooltip inline text={PEER_GROUP_TIP} />
            </div>
          )}
          <div className="actions">
            {(d.carrier === "awaitingRequest" || d.carrier === "workOrder") && wo?.assignmentId && wo.workOrderVersionId ? (
              <a className="btn btn-ghost btn-sm" href={fboWorkOrderUrl(wo.assignmentId, wo.workOrderId, wo.workOrderVersionId)} target="_blank" rel="noopener noreferrer" title="The work order carries the journey: open it in FBO.">
                <span className="material-symbols-rounded">open_in_new</span>Open WO in FBO
              </a>
            ) : d.request ? (
              <a className="btn btn-ghost btn-sm" href={`${FBO_REQUEST_URL}${encodeURIComponent(d.request.id)}`} target="_blank" rel="noopener noreferrer">
                <span className="material-symbols-rounded">open_in_new</span>Open in FBO
              </a>
            ) : null}
            {wo?.workOrderNumber && <CopyWoButton workOrderNumber={wo.workOrderNumber} />}
            {onAskClaude && (
              <button type="button" className="btn btn-primary btn-sm" onClick={onAskClaude}>
                <span className="material-symbols-rounded">forum</span>Ask Claude why
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
